import React, { Component } from 'react'
import { Form } from 'react-bootstrap'
import '../App.css'
import { embedComponent } from '../embed'
import Field from '../components/Field'
import CurrencyField from '../components/CurrencyField'
import formatCurrency from '../utils/formatters/formatCurrency'
import numberWithLabelChoices from '../utils/forms/numberWithLabelChoices'
import requiredPaymentToGoal from '../utils/calculations/requiredPaymentToGoal'
import CalculatorResults from '../components/CalculatorResults/index'
import CalculatorResult from '../components/CalculatorResult/index'
import Calculator from '../components/Calculator/index'
import PercentageField from '../components/PercentageField/index'
import CalculatorButtons from '../components/CalculatorButtons/index'
import { minMaxValidator } from '../utils/validators/index'
import formatPercentage from '../utils/formatters/formatPercentage'
import { DEFAULT_MAX_DOLLAR_VALUE } from '../utils/validators'

const popoverMessages = {
  savingsGoal: {
    title: 'Savings goal',
    body: [
      'This is the total amount you want to have in a savings account.',
    ],
  },
  currentSavings: {
    title: 'Current savings',
    body: [
      'This is the amount you have now in your savings fund. It does not include other savings like 401k or stocks.',
    ],
  },
  timeToGoal: {
    title: 'Time to goal',
    body: [
      'This is the time you are giving yourself to reach your savings goal.',
      'The shorter you make it, the higher the monthly contributions required will be.',
    ],
  },
  interestOnSavings: {
    title: 'Interest on savings',
    body: [
      'This is the interest rate you get on your savings.',
    ],
  },
  monthlySavingsAmount: {
    title: 'You need to save this amount/month',
    body: [
      'This is the amount you need to set aside each month for the number of months you indicated.',
    ],
  },
}

// Create select input options for months dropdowns.
const timeToGoalValues = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
const timeToGoalOptions = numberWithLabelChoices(timeToGoalValues, ' years')

class SavingsGoalCalculator extends Component {
  constructor (props) {
    super(props);

    this.state = SavingsGoalCalculator.getInitialState();

    this.clearState = this.clearState.bind(this);
    this.onValidation = this.onValidation.bind(this);
    this.getCalculatorValues = this.getCalculatorValues.bind(this);
    this.setCalculatorValues = this.setCalculatorValues.bind(this);
    this.removeEventListeners = this.removeEventListeners.bind(this);
  }

  static getInitialState() {
    return {
      savingsGoal: 0,
      currentSavings: null,
      timeToGoal: 2,
      interestOnSavings: null,
      fieldErrors: {},
    }
  }

  componentDidMount() {
    document.addEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.addEventListener('getCalculatorValuesSavingsGoalCalculator', this.getCalculatorValues, true);
    document.addEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  getCalculatorValues (event) {
    this.removeEventListeners();

    // Build newObj off state and exclude fieldErrors
    let newObj = {};
    Object.keys(SavingsGoalCalculator.getInitialState()).forEach(key => {
      if (key !== 'fieldErrors') {
        newObj[key] = this.state[key];
      }
    });

    newObj.calculatedMonthlySavingsAmount = this.calculateMonthlySavingsAmount();

    document.dispatchEvent(new CustomEvent('receiveCalculatorStateSavingsGoalCalculator', { detail: newObj}));
  }

  setCalculatorValues (event) {
    this.handleChange(event.detail.value, event.detail.name);
  }

  removeEventListeners () {
    document.removeEventListener('getCalculatorValuesSavingsGoalCalculator', this.getCalculatorValues, true);
    document.removeEventListener('setCalculatorValues', this.setCalculatorValues, true);
    document.removeEventListener('removeCalculatorEvents', this.removeEventListeners, true);
  }

  handleChange (value, fieldName) {
    this.setState({ [fieldName]: value });
  }

  clearState () {
    this.setState(SavingsGoalCalculator.getInitialState());
  }

  onValidation(name, error) {
    if (typeof error !== 'undefined') {
      // Has errors
      if (!this.state.fieldErrors[name]) {
        this.setState({ fieldErrors: Object.assign({}, this.state.fieldErrors, { [name]: error }) });
      }
    } else {
      // No errors
      if (this.state.fieldErrors[name]) {
        const newErrors = Object.assign({}, this.state.fieldErrors);
        delete newErrors[name];
        this.setState({ fieldErrors: newErrors });
      }
    }
  }

  calculateMonthlySavingsAmount () {
    const { currentSavings, interestOnSavings, savingsGoal, timeToGoal } = this.state;

    const rawSavingsGoal = savingsGoal;

    if (!rawSavingsGoal || !interestOnSavings) {
      return undefined;
    }

    return requiredPaymentToGoal(rawSavingsGoal, interestOnSavings / 100, timeToGoal, currentSavings || 0);
  }

  render () {
    const {
      savingsGoal,
      currentSavings,
      timeToGoal,
      interestOnSavings,
    } = this.state

    const calculatedMonthlySavingsAmount = this.calculateMonthlySavingsAmount();
    const hasErrors = Object.keys(this.state.fieldErrors).length > 0;

    return (
      <Calculator title="Savings Goal Calculator">
        <div className="calculator-body">
          <Form>
            <CurrencyField
              name="savingsGoal"
              label="Savings Goal"
              value={savingsGoal}
              onChange={(e, value) => this.handleChange(value, 'savingsGoal') }
              popover={popoverMessages.savingsGoal}
            />

            <CurrencyField
              name="currentSavings"
              label="Current Savings"
              value={currentSavings}
              onChange={(e, value) => this.handleChange(value, 'currentSavings') }
              popover={popoverMessages.currentSavings}
              validator={minMaxValidator(1, DEFAULT_MAX_DOLLAR_VALUE, formatCurrency)}
              onValidation={this.onValidation}
            />

            <Field
              type="select"
              name="timeToGoal"
              label="Time to Goal"
              value={timeToGoal}
              onChange={(e, value) => this.handleChange(value, 'timeToGoal')}
              popover={popoverMessages.timeToGoal}
              choices={timeToGoalOptions}
            />

            <PercentageField
              name="interestOnSavings"
              label="Interest on Savings (%)"
              value={interestOnSavings}
              onChange={(e, value) => this.handleChange(value, 'interestOnSavings') }
              popover={popoverMessages.interestOnSavings}
              validator={minMaxValidator(0.001, 30, formatPercentage)}
              onValidation={this.onValidation}
            />
          </Form>
          <CalculatorResults>
            <CalculatorResult
              label={'You need to save this amount/month'}
              value={formatCurrency(Math.ceil((!hasErrors && calculatedMonthlySavingsAmount) || 0))}
              popover={popoverMessages.monthlySavingsAmount}
            />
          </CalculatorResults>
          <CalculatorButtons onClear={this.clearState} />
        </div>
      </Calculator>
    )
  }
}

embedComponent(SavingsGoalCalculator, 'bmm-savings-goal-calc', 'bmm-savings-goal-calc-embed')

export default SavingsGoalCalculator



// WEBPACK FOOTER //
// ./src/calculators/SavingsGoalCalculator.js